import React, { useState } from "react";
import { Link } from "react-router-dom";
import "../../css/partner.scss";
import Header from "../common/Header";
import Footer from "../common/Footer";
import { useTranslation } from "react-i18next";
import { sendMessage } from "../../apis/SendMessageAPI";
import { buildPartnerApplicationMessage } from "../../utils/footerInquiryMessage";

const hasRichHtml = (s) =>
  typeof s === "string" && /<\/?(strong|b|em|i|ul|ol|li|br|a|sup)\b/i.test(s);

const renderSectionBody = (sec) => (
  <>
    {(sec.paragraphs || []).map((p, i) =>
      hasRichHtml(String(p)) ? (
        <p
          key={`p-${i}`}
          className="partner__p"
          dangerouslySetInnerHTML={{ __html: p }}
        />
      ) : (
        <p key={`p-${i}`} className="partner__p">
          {p}
        </p>
      )
    )}
    {sec.listLead && <p className="partner__list-lead">{sec.listLead}</p>}
    {Array.isArray(sec.list) && sec.list.length > 0 && (
      <ul className="partner__list partner__list--check">
        {sec.list.map((li, j) => (
          <li key={`li-${j}`}>{li}</li>
        ))}
      </ul>
    )}
    {Array.isArray(sec.ordered) && sec.ordered.length > 0 && (
      <ol className="partner__steps">
        {sec.ordered.map((li, j) => (
          <li key={`ol-${j}`}>
            <span>{li}</span>
          </li>
        ))}
      </ol>
    )}
  </>
);

const Partner = () => {
  const { t } = useTranslation();
  const sections = t("partner.sections", { returnObjects: true }) || [];
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [platform, setPlatform] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: null, text: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: null, text: "" });
    setSubmitting(true);
    try {
      const text = buildPartnerApplicationMessage(
        name,
        email,
        platform,
        message
      );
      await sendMessage(text);
      setFeedback({ type: "success", text: t("partner.form.submitSuccess") });
      setName("");
      setEmail("");
      setPlatform("");
      setMessage("");
    } catch (err) {
      console.error(err);
      setFeedback({ type: "error", text: t("partner.form.submitError") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="partner-page">
      <Header />
      <main className="partner__container">
        <div className="partner__layout">
          <div className="partner__main">
            <nav className="partner__breadcrumb" aria-label="Breadcrumb">
              <Link to="/">{t("partner.breadcrumbHome")}</Link>
            </nav>
            <h1 className="partner__title">{t("partner.title")}</h1>
            <p className="partner__intro">{t("partner.intro")}</p>

            {sections.map((sec, idx) => (
              <section className="partner-sec" key={`sec-${idx}`}>
                <h2 className="partner-sec__title">{sec.title}</h2>
                {sec.boxed ? (
                  <div className="partner-info-box">
                    {renderSectionBody(sec)}
                  </div>
                ) : (
                  renderSectionBody(sec)
                )}
              </section>
            ))}
          </div>

          <aside
            className="partner__aside"
            aria-label={t("partner.form.title")}
          >
            <form className="partner-form" onSubmit={handleSubmit} noValidate>
              <h2 className="partner-form__title">{t("partner.form.title")}</h2>

              <label className="partner-form__label" htmlFor="partner-name">
                {t("partner.form.nameLabel")}
                <span className="partner-form__req" aria-hidden="true">
                  {" "}
                  {t("partner.form.requiredStar")}
                </span>
              </label>
              <input
                id="partner-name"
                className="partner-form__input"
                type="text"
                name="name"
                autoComplete="name"
                placeholder={t("partner.form.namePlaceholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />

              <label className="partner-form__label" htmlFor="partner-email">
                {t("partner.form.emailLabel")}
                <span className="partner-form__req" aria-hidden="true">
                  {" "}
                  {t("partner.form.requiredStar")}
                </span>
              </label>
              <input
                id="partner-email"
                className="partner-form__input"
                type="email"
                name="email"
                autoComplete="email"
                placeholder={t("partner.form.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              <label className="partner-form__label" htmlFor="partner-platform">
                {t("partner.form.platformLabel")}
              </label>
              <input
                id="partner-platform"
                className="partner-form__input"
                type="text"
                name="platform"
                autoComplete="url"
                placeholder={t("partner.form.platformPlaceholder")}
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
              />

              <label className="partner-form__label" htmlFor="partner-message">
                {t("partner.form.messageLabel")}
                <span className="partner-form__req" aria-hidden="true">
                  {" "}
                  {t("partner.form.requiredStar")}
                </span>
              </label>
              <textarea
                id="partner-message"
                className="partner-form__textarea"
                name="message"
                rows={6}
                placeholder={t("partner.form.messagePlaceholder")}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
              />

              {feedback.text ? (
                <p
                  className={`partner-form__feedback partner-form__feedback--${feedback.type}`}
                  role="status"
                >
                  {feedback.text}
                </p>
              ) : null}

              <button
                className="partner-form__submit"
                type="submit"
                disabled={submitting}
                aria-busy={submitting || undefined}
              >
                {submitting
                  ? t("partner.form.submitting")
                  : t("partner.form.submit")}
              </button>
            </form>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Partner;
